// API クライアントユーティリティ
// このファイルは手動編集可能です

import { Duration, Timestamp } from "@bufbuild/protobuf";
import { shouldShowDebugLogs } from "../../config/EnvironmentConfig";
import { structDocumentationData } from "../generated/StructDocs";

// ヘッダー情報の管理
export interface AdminClientHeaders {
  userID?: string;
  sessionID?: string;
  requestID?: string;
  traceID?: string;
  timestamp?: string;
  userAgent?: string;
  ipAddress?: string;
  platform?: number;
  version?: string;
  deviceID?: string;
  language?: string;
  token?: string;
  tokenType?: string;
  roles?: string[];
  permissions?: string[];
  expiresDate?: string;
  // Developer関連
  developerID?: string;
  // Client情報（header_context.goと対応）
  clientUserTime?: number; // Unix timestamp
  clientAppVersion?: string;
  clientAssetVersion?: string; // アセットバージョン
  clientDeviceName?: string; // "Windows 11", "macOS 14.0", "iOS 17.1" など（デバイス名 + バージョン）
  clientOSVersion?: string; // "11", "14.0", "17.1" など（バージョンのみ）
}

// グローバルヘッダー状態
let globalHeaders: AdminClientHeaders = {};

// ヘッダー管理ユーティリティ
export const HeaderUtils = {
  // ヘッダーを設定
  setHeaders(headers: Partial<AdminClientHeaders>) {
    globalHeaders = { ...globalHeaders, ...headers };
    console.log("📋 Admin Client ヘッダー設定:", globalHeaders);
  },

  // 特定のヘッダーを取得
  getHeader(
    key: keyof AdminClientHeaders
  ): string | number | string[] | undefined {
    return globalHeaders[key];
  },

  // 全てのヘッダーを取得
  getAllHeaders(): AdminClientHeaders {
    return { ...globalHeaders };
  },

  // ヘッダーをクリア
  clearHeaders() {
    globalHeaders = {};
    console.log("📋 Admin Client ヘッダーをクリア");
  },

  // 自動ヘッダー生成（リクエストごとに呼ばれる）
  generateRequestHeaders(): Record<string, string> {
    const headers: Record<string, string> = {};

    // 基本ヘッダー
    if (globalHeaders.userID) headers["x-user-id"] = globalHeaders.userID;
    if (globalHeaders.sessionID)
      headers["x-session-id"] = globalHeaders.sessionID;
    if (globalHeaders.requestID)
      headers["x-request-id"] = globalHeaders.requestID;
    if (globalHeaders.traceID) headers["x-trace-id"] = globalHeaders.traceID;

    // タイムスタンプ（現在時刻またはカスタム値）
    const timestamp =
      globalHeaders.timestamp || Math.floor(Date.now() / 1000).toString();
    headers["x-timestamp"] = timestamp;

    // クライアント情報
    if (globalHeaders.userAgent)
      headers["x-user-agent"] = globalHeaders.userAgent;
    if (globalHeaders.ipAddress)
      headers["x-ip-address"] = globalHeaders.ipAddress;
    // x-platform は必須ヘッダー（デフォルト: 1 = Admin）
    headers["x-platform"] = (globalHeaders.platform ?? 1).toString();
    if (globalHeaders.version) headers["x-version"] = globalHeaders.version;
    if (globalHeaders.deviceID) headers["x-device-id"] = globalHeaders.deviceID;

    // 認証情報
    if (globalHeaders.token) headers["x-token"] = globalHeaders.token;
    if (globalHeaders.tokenType)
      headers["x-token-type"] = globalHeaders.tokenType;
    if (globalHeaders.roles && globalHeaders.roles.length > 0) {
      headers["x-roles"] = globalHeaders.roles.join(",");
    }
    if (globalHeaders.permissions && globalHeaders.permissions.length > 0) {
      headers["x-permissions"] = globalHeaders.permissions.join(",");
    }
    if (globalHeaders.expiresDate)
      headers["x-expires-at"] = globalHeaders.expiresDate;

    // Developer情報
    if (globalHeaders.developerID)
      headers["x-developer-id"] = globalHeaders.developerID;

    // Client詳細情報（header_context.goの必須ヘッダーに対応）
    // x-client-user-time は必須ヘッダー（デフォルト: 現在のUnix timestamp）
    headers["x-client-user-time"] = (
      globalHeaders.clientUserTime ?? Math.floor(Date.now() / 1000)
    ).toString();
    // x-client-app-version は必須ヘッダー（デフォルト: "1.0.0"）
    headers["x-client-app-version"] = globalHeaders.clientAppVersion || "1.0.0";
    // x-client-asset-version は必須ヘッダー（デフォルト: "1.0.0"）
    headers["x-client-asset-version"] =
      globalHeaders.clientAssetVersion || "1.0.0";
    // x-client-device-name は必須ヘッダー（デフォルト: "Admin Web"）
    headers["x-client-device-name"] =
      globalHeaders.clientDeviceName || "Admin Web";
    // x-client-os-version は必須ヘッダー（デフォルト: "1.0"）
    headers["x-client-os-version"] = globalHeaders.clientOSVersion || "1.0";
    // x-language は必須ヘッダー（デフォルト: "ja"）
    headers["x-language"] = globalHeaders.language || "ja";

    // デフォルト値の設定
    if (!headers["x-request-id"]) {
      headers["x-request-id"] = `req_${Date.now()}_${Math.random()
        .toString(36)
        .substr(2, 9)}`;
    }
    if (!headers["x-trace-id"]) {
      headers["x-trace-id"] = `trace_${Date.now()}_${Math.random()
        .toString(36)
        .substr(2, 9)}`;
    }

    return headers;
  },

  // ブラウザ情報から自動的にヘッダーを設定
  setHeadersFromBrowser() {
    if (typeof window !== "undefined") {
      const deviceInfo = this.getDeviceInfo();

      const headers: Partial<AdminClientHeaders> = {
        userAgent: navigator.userAgent,
        language: navigator.language,
        platform: 1, // Web platform
        version: "1.0.0", // アプリケーションバージョン
        clientAppVersion: "1.0.0", // デフォルトのクライアントアプリバージョン
        clientDeviceName: deviceInfo.deviceName,
        clientOSVersion: deviceInfo.osVersion,
        clientUserTime: Math.floor(Date.now() / 1000),
      };

      // IPアドレスは通常取得できないため、プレースホルダー
      if (
        window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1"
      ) {
        headers.ipAddress = "127.0.0.1";
      }

      this.setHeaders(headers);
    }
  },

  // ブラウザ情報から自動的にヘッダーを設定（非同期版、より正確）
  async setHeadersFromBrowserAsync() {
    if (typeof window !== "undefined") {
      const deviceInfo = await this.getDeviceInfoAsync();

      const headers: Partial<AdminClientHeaders> = {
        userAgent: navigator.userAgent,
        language: navigator.language,
        platform: 1, // Web platform
        version: "1.0.0",
        clientAppVersion: "1.0.0",
        clientDeviceName: deviceInfo.deviceName,
        clientOSVersion: deviceInfo.osVersion,
        clientUserTime: Math.floor(Date.now() / 1000),
      };

      if (
        window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1"
      ) {
        headers.ipAddress = "127.0.0.1";
      }

      this.setHeaders(headers);
    }
  },

  // デバイス情報を取得（モバイルは機種名、PCはOS名+バージョン）
  getDeviceInfo(): { deviceName: string; osVersion: string } {
    if (typeof window === "undefined") {
      return { deviceName: "unknown", osVersion: "unknown" };
    }

    const userAgent = navigator.userAgent;

    // iOS デバイスの検出（iPhone, iPad）
    const iosMatch = userAgent.match(/OS (\d+[._]\d+[._]?\d*) like Mac OS X/);
    if (iosMatch) {
      const version = iosMatch[1].replace(/_/g, ".");

      if (userAgent.includes("iPad")) {
        const screenWidth = window.screen.width;
        const screenHeight = window.screen.height;
        const maxDimension = Math.max(screenWidth, screenHeight);

        if (maxDimension >= 2732) {
          return { deviceName: 'iPad Pro 12.9"', osVersion: version };
        } else if (maxDimension >= 2388) {
          return { deviceName: 'iPad Pro 11"', osVersion: version };
        } else if (maxDimension >= 2266) {
          return { deviceName: "iPad Air", osVersion: version };
        } else {
          return { deviceName: "iPad", osVersion: version };
        }
      } else if (userAgent.includes("iPhone")) {
        const screenWidth = window.screen.width;
        const screenHeight = window.screen.height;
        const maxDimension = Math.max(screenWidth, screenHeight);

        if (maxDimension >= 932) {
          return { deviceName: "iPhone 15 Pro Max", osVersion: version };
        } else if (maxDimension >= 852) {
          return { deviceName: "iPhone 15 Plus", osVersion: version };
        } else if (maxDimension >= 844) {
          return { deviceName: "iPhone 15 Pro", osVersion: version };
        } else if (maxDimension >= 812) {
          return { deviceName: "iPhone 15", osVersion: version };
        } else {
          return { deviceName: "iPhone", osVersion: version };
        }
      } else {
        return { deviceName: "iOS Device", osVersion: version };
      }
    }

    // Android デバイスの検出
    const androidMatch = userAgent.match(/Android (\d+\.?\d*\.?\d*)/);
    if (androidMatch) {
      const version = androidMatch[1];
      const deviceMatch = userAgent.match(/Android [^;]+;\s*([^)]+)\)/);
      if (deviceMatch && deviceMatch[1]) {
        let deviceModel = deviceMatch[1].trim();

        if (deviceModel.startsWith("SM-")) {
          deviceModel = this.getSamsungModelName(deviceModel);
        } else if (deviceModel.includes("SO-") || deviceModel.includes("SOV")) {
          deviceModel = this.getSonyModelName(deviceModel);
        }

        return { deviceName: deviceModel, osVersion: version };
      }

      return { deviceName: "Android Device", osVersion: version };
    }

    // Windows の検出
    if (userAgent.includes("Windows NT 10.0")) {
      const buildMatch = userAgent.match(/Windows NT 10\.0.*?(\d{5,})/);
      if (buildMatch) {
        const buildNumber = parseInt(buildMatch[1]);
        if (buildNumber >= 22000) {
          return { deviceName: "Windows 11", osVersion: "11" };
        }
        return { deviceName: "Windows 10", osVersion: "10" };
      }
      return { deviceName: "Windows 10/11", osVersion: "10/11" };
    }
    if (userAgent.includes("Windows NT 6.3")) {
      return { deviceName: "Windows 8.1", osVersion: "8.1" };
    }
    if (userAgent.includes("Windows NT 6.2")) {
      return { deviceName: "Windows 8", osVersion: "8" };
    }
    if (userAgent.includes("Windows NT 6.1")) {
      return { deviceName: "Windows 7", osVersion: "7" };
    }

    // macOS
    const macMatch = userAgent.match(/Mac OS X (\d+[._]\d+[._]?\d*)/);
    if (macMatch) {
      const version = macMatch[1].replace(/_/g, ".");
      return { deviceName: `macOS ${version}`, osVersion: version };
    }

    // Linux
    if (userAgent.includes("Linux")) {
      return { deviceName: "Linux", osVersion: "unknown" };
    }

    return { deviceName: "unknown", osVersion: "unknown" };
  },

  // Samsung Galaxy モデル名変換
  getSamsungModelName(modelCode: string): string {
    const samsungModels: Record<string, string> = {
      "SM-S921": "Galaxy S24",
      "SM-S926": "Galaxy S24+",
      "SM-S928": "Galaxy S24 Ultra",
      "SM-S911": "Galaxy S23",
      "SM-S916": "Galaxy S23+",
      "SM-S918": "Galaxy S23 Ultra",
      "SM-G991": "Galaxy S21",
      "SM-G996": "Galaxy S21+",
      "SM-G998": "Galaxy S21 Ultra",
      "SM-A546": "Galaxy A54",
      "SM-A536": "Galaxy A53",
    };

    const shortCode = modelCode.substring(0, 7);
    return samsungModels[shortCode] || modelCode;
  },

  // Sony Xperia モデル名変換
  getSonyModelName(modelCode: string): string {
    const sonyModels: Record<string, string> = {
      "SO-51D": "Xperia 1 V",
      "SO-52D": "Xperia 5 V",
      "SO-53D": "Xperia 10 V",
      "SO-51C": "Xperia 1 IV",
      "SO-52C": "Xperia 5 IV",
      "SO-53C": "Xperia 10 IV",
      SOV43: "Xperia 1 V",
      SOV42: "Xperia 5 V",
    };

    return sonyModels[modelCode] || modelCode;
  },

  // OSバージョンを取得（互換性のため残す）
  getOSVersion(): string {
    const deviceInfo = this.getDeviceInfo();
    return deviceInfo.deviceName;
  },

  // デバイス情報を正確に検出する非同期版（User-Agent Client Hints API使用）
  async getDeviceInfoAsync(): Promise<{
    deviceName: string;
    osVersion: string;
  }> {
    if (typeof window === "undefined") {
      return { deviceName: "unknown", osVersion: "unknown" };
    }

    const userAgent = navigator.userAgent;

    if (
      navigator.userAgentData &&
      typeof navigator.userAgentData.getHighEntropyValues === "function"
    ) {
      try {
        const uaData = await navigator.userAgentData.getHighEntropyValues([
          "platform",
          "platformVersion",
          "model",
          "uaFullVersion",
        ]);

        if (uaData.mobile && uaData.model) {
          const osVersion = this.extractVersionFromUA(userAgent);
          let modelName = uaData.model;
          if (modelName.startsWith("SM-")) {
            modelName = this.getSamsungModelName(modelName);
          } else if (modelName.includes("SO-") || modelName.includes("SOV")) {
            modelName = this.getSonyModelName(modelName);
          }

          return { deviceName: modelName, osVersion };
        }

        if (uaData.platform === "Windows" && uaData.platformVersion) {
          const parts = uaData.platformVersion.split(".");
          const majorVersion = parseInt(parts[0]);

          if (majorVersion >= 13) {
            return { deviceName: "Windows 11", osVersion: "11" };
          }
          return { deviceName: "Windows 10", osVersion: "10" };
        }
      } catch (e) {
        console.warn("User-Agent Client Hints APIでのOS検出に失敗:", e);
      }
    }

    return this.getDeviceInfo();
  },

  // UserAgentからOSバージョンを抽出
  extractVersionFromUA(userAgent: string): string {
    const androidMatch = userAgent.match(/Android (\d+\.?\d*\.?\d*)/);
    if (androidMatch) return androidMatch[1];

    const iosMatch = userAgent.match(/OS (\d+[._]\d+[._]?\d*) like Mac OS X/);
    if (iosMatch) return iosMatch[1].replace(/_/g, ".");

    return "unknown";
  },

  async getOSVersionAsync(): Promise<string> {
    const deviceInfo = await this.getDeviceInfoAsync();
    return deviceInfo.deviceName;
  },

  // Developer認証情報を設定
  setDeveloperAuth(developerID: string) {
    this.setHeaders({
      developerID: developerID,
    });
    console.log("👤 Developer認証情報を設定:", { developerID });
  },

  // Client情報の詳細設定
  setClientInfo(info: {
    clientAppVersion?: string;
    clientDeviceName?: string;
    clientOSVersion?: string;
    clientUserTime?: number;
  }) {
    this.setHeaders(info);
    console.log("📱 Client情報を設定:", info);
  },
};

// Response wrapper type
export interface ProtoResponse<T> {
  data?: T;
  error?: string;
}

// 型変換ユーティリティ
export const TypeUtils = {
  // Convert protobuf Timestamp to Date
  timestampToDate(timestamp?: Timestamp): Date | null {
    if (!timestamp) return null;
    return timestamp.toDate();
  },

  // Convert Date to protobuf Timestamp
  dateToTimestamp(date: Date | null): Timestamp | undefined {
    if (!date) return undefined;
    return Timestamp.fromDate(date);
  },

  // Convert protobuf Duration to TypeScript Duration interface
  protoToTSDuration(duration?: Duration): { nanoseconds: number } | null {
    if (!duration) return null;
    return {
      nanoseconds: Number(duration.seconds) * 1000000000 + duration.nanos,
    };
  },

  // Convert TypeScript Duration interface to protobuf Duration
  tsToDuration(duration: { nanoseconds: number } | null): Duration | undefined {
    if (!duration) return undefined;
    const seconds = Math.floor(duration.nanoseconds / 1000000000);
    const nanos = duration.nanoseconds % 1000000000;
    return new Duration({ seconds: BigInt(seconds), nanos });
  },

  // Convert protobuf message to plain object with proper type conversion
  toPlainObject(message: any): any {
    // Use message.toJson() method with enumAsInteger option to keep enum values as numbers
    // emitDefaultValues: true ensures that default values (like enum 0) are included in the output
    const json = message.toJson({ enumAsInteger: true, emitDefaultValues: true });

    if (shouldShowDebugLogs()) {
      console.log("🔍 Raw toJson() output:", json);
    }

    const converted = this.convertProtobufTypes(json);

    if (shouldShowDebugLogs()) {
      console.log("🔍 After convertProtobufTypes:", converted);
    }

    const result = this.convertKeysToCamelCase(converted);

    if (shouldShowDebugLogs()) {
      console.log("🔍 After convertKeysToCamelCase:", result);
    }

    return result;
  },

  // Convert snake_case keys to camelCase keys recursively
  convertKeysToCamelCase(obj: any): any {
    if (obj === null || obj === undefined) return obj;

    if (obj instanceof Date) {
      return obj;
    }

    if (Array.isArray(obj)) {
      return obj.map((item) => this.convertKeysToCamelCase(item));
    }

    if (typeof obj === "object") {
      const result: any = {};
      for (const [key, value] of Object.entries(obj)) {
        const camelKey = this.snakeToCamel(key);
        result[camelKey] = this.convertKeysToCamelCase(value);
      }
      return result;
    }

    return obj;
  },

  // Convert snake_case to camelCase
  snakeToCamel(str: string): string {
    return str.replace(/_([a-z])/g, (match, letter) => letter.toUpperCase());
  },

  // Recursively convert protobuf types to TypeScript types
  convertProtobufTypes(obj: any): any {
    if (obj === null || obj === undefined) return obj;

    // Handle protobuf Duration objects by constructor name
    if (
      typeof obj === "object" &&
      obj !== null &&
      obj.constructor &&
      obj.constructor.name &&
      obj.constructor.name.includes("Duration")
    ) {
      const seconds = parseInt(obj.seconds || "0");
      const nanos = parseInt(obj.nanos || "0");

      let totalNanos = seconds * 1000000000 + nanos;

      if (seconds < 0 && nanos > 0) {
        totalNanos = seconds * 1000000000 - nanos;
      } else if (seconds > 0 && nanos < 0) {
        totalNanos = seconds * 1000000000 + nanos;
      }

      return this.formatDurationFromNanos(totalNanos);
    }

    // Handle protobuf Timestamp objects by constructor name
    if (
      typeof obj === "object" &&
      obj !== null &&
      obj.constructor &&
      obj.constructor.name &&
      obj.constructor.name.includes("Timestamp")
    ) {
      const seconds = parseInt(obj.seconds || "0");
      const nanos = parseInt(obj.nanos || "0");

      if (seconds === 0 && nanos === 0) {
        return null;
      }

      return new Date(seconds * 1000 + nanos / 1000000);
    }

    // Handle duration strings (keep as string for compatibility)
    if (typeof obj === "string" && this.isDurationString(obj)) {
      return obj;
    }

    // Handle timestamp strings
    if (typeof obj === "string" && this.isTimestampString(obj)) {
      if (obj === "0001-01-01T00:00:00Z" || obj.startsWith("0001-01-01T")) {
        return null;
      }
      return new Date(obj);
    }

    // Handle protobuf objects (Timestamp/Duration)
    if (typeof obj === "object" && obj !== null) {
      const keys = Object.keys(obj);

      if (keys.length === 0) {
        return null;
      }
    }

    // Handle arrays
    if (Array.isArray(obj)) {
      return obj.map((item) => this.convertProtobufTypes(item));
    }

    // Handle objects
    if (typeof obj === "object") {
      const result: any = {};
      for (const [key, value] of Object.entries(obj)) {
        result[key] = this.convertProtobufTypes(value);
      }
      return result;
    }

    return obj;
  },

  // Check if a string looks like a timestamp
  isTimestampString(str: string): boolean {
    return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z?$/.test(str);
  },

  // Check if this is a timestamp-related field by name
  isTimestampField(key: string): boolean {
    const timestampFields = [
      "create_date",
      "update_date",
      "createDate",
      "updateDate",
      "timestamp",
      "time",
    ];
    return timestampFields.some((field) =>
      key.toLowerCase().includes(field.toLowerCase())
    );
  },

  // Check if this is a duration-related field by type information
  isDurationField(parentTypeName: string, fieldName: string): boolean {
    const structInfo = structDocumentationData.find(
      (s) =>
        s.typeName === parentTypeName ||
        s.displayName.includes(parentTypeName) ||
        s.typeName.toLowerCase() === parentTypeName.toLowerCase()
    );

    if (!structInfo) {
      return fieldName.toLowerCase().includes("duration");
    }

    const fieldInfo = structInfo.fields.find(
      (f) =>
        f.name === fieldName ||
        f.jsonName === fieldName ||
        f.name.toLowerCase() === fieldName.toLowerCase()
    );

    if (!fieldInfo) {
      return fieldName.toLowerCase().includes("duration");
    }

    return (
      fieldInfo.type === "Duration" ||
      fieldInfo.frontendType === "Duration" ||
      fieldInfo.type.includes("Duration")
    );
  },

  // Check if this is a duration-related field by name (fallback)
  isDurationContext(key: string): boolean {
    const durationFields = ["duration", "timeout", "delay", "interval"];
    return durationFields.some((field) =>
      key.toLowerCase().includes(field.toLowerCase())
    );
  },

  // Check if a string looks like a duration
  isDurationString(str: string): boolean {
    return (
      /^-?\d+(\.\d+)?(ns|us|µs|ms|s|m|h)$/.test(str) ||
      /^-?\d+h(\d+(\.\d+)?m)?(\d+(\.\d+)?s)?$/.test(str) ||
      /^-?\d+m(\d+(\.\d+)?s)?$/.test(str) ||
      /^-?\d+(\.\d+)?s$/.test(str) ||
      str === "0" ||
      str === "0s"
    );
  },

  // Parse duration string to nanoseconds
  parseDurationString(str: string): number {
    if (str === "0" || str === "0s") return 0;

    const isNegative = str.startsWith("-");
    const cleanStr = isNegative ? str.substring(1) : str;

    const simpleMatch = cleanStr.match(/^(\d+(?:\.\d+)?)(ns|us|µs|ms|s|m|h)$/);
    if (simpleMatch) {
      const value = parseFloat(simpleMatch[1]);
      const unit = simpleMatch[2];

      let nanos = 0;
      switch (unit) {
        case "ns":
          nanos = value;
          break;
        case "us":
        case "µs":
          nanos = value * 1000;
          break;
        case "ms":
          nanos = value * 1000000;
          break;
        case "s":
          nanos = value * 1000000000;
          break;
        case "m":
          nanos = value * 60 * 1000000000;
          break;
        case "h":
          nanos = value * 3600 * 1000000000;
          break;
        default:
          nanos = 0;
      }

      return isNegative ? -nanos : nanos;
    }

    let totalNanos = 0;
    const parts = cleanStr.match(/(\d+(?:\.\d+)?)(ns|us|µs|ms|s|m|h)/g);
    if (parts) {
      for (const part of parts) {
        const match = part.match(/^(\d+(?:\.\d+)?)(ns|us|µs|ms|s|m|h)$/);
        if (match) {
          const value = parseFloat(match[1]);
          const unit = match[2];

          switch (unit) {
            case "ns":
              totalNanos += value;
              break;
            case "us":
            case "µs":
              totalNanos += value * 1000;
              break;
            case "ms":
              totalNanos += value * 1000000;
              break;
            case "s":
              totalNanos += value * 1000000000;
              break;
            case "m":
              totalNanos += value * 60 * 1000000000;
              break;
            case "h":
              totalNanos += value * 3600 * 1000000000;
              break;
          }
        }
      }
    }

    return isNegative ? -totalNanos : totalNanos;
  },

  // Format nanoseconds to Go duration string format
  formatDurationFromNanos(totalNanos: number): string {
    if (totalNanos === 0) return "0s";

    const isNegative = totalNanos < 0;
    const absNanos = Math.abs(totalNanos);

    if (absNanos < 1000) {
      return `${isNegative ? "-" : ""}${absNanos}ns`;
    } else if (absNanos < 1000000) {
      return `${isNegative ? "-" : ""}${absNanos / 1000}µs`;
    } else if (absNanos < 1000000000) {
      return `${isNegative ? "-" : ""}${absNanos / 1000000}ms`;
    } else if (absNanos < 60 * 1000000000) {
      return `${isNegative ? "-" : ""}${absNanos / 1000000000}s`;
    } else if (absNanos < 3600 * 1000000000) {
      const minutes = Math.floor(absNanos / (60 * 1000000000));
      const remainingNanos = absNanos % (60 * 1000000000);
      if (remainingNanos === 0) {
        return `${isNegative ? "-" : ""}${minutes}m`;
      } else {
        const seconds = remainingNanos / 1000000000;
        return `${isNegative ? "-" : ""}${minutes}m${seconds}s`;
      }
    } else {
      const hours = Math.floor(absNanos / (3600 * 1000000000));
      const remainingNanos = absNanos % (3600 * 1000000000);
      const minutes = Math.floor(remainingNanos / (60 * 1000000000));
      const seconds = (remainingNanos % (60 * 1000000000)) / 1000000000;

      let result = `${isNegative ? "-" : ""}${hours}h`;
      if (minutes > 0) result += `${minutes}m`;
      if (seconds > 0) result += `${seconds}s`;
      return result;
    }
  },

  // Prepare data for sending (convert TS types to protobuf types)
  prepareForSend(data: any): any {
    if (data === null || data === undefined) return data;

    if (data instanceof Uint8Array) {
      return data;
    }

    if (data instanceof Date) {
      return this.dateToTimestamp(data);
    }

    if (typeof data === "string" && this.isDurationString(data)) {
      return data;
    }

    if (data && typeof data === "object" && "nanoseconds" in data) {
      const nanos = data.nanoseconds;
      if (nanos === 0) return "0s";

      if (nanos < 1000) return `${nanos}ns`;
      if (nanos < 1000000) return `${nanos / 1000}µs`;
      if (nanos < 1000000000) return `${nanos / 1000000}ms`;

      const seconds = nanos / 1000000000;
      if (seconds < 60) return `${seconds}s`;
      if (seconds < 3600) {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return remainingSeconds > 0
          ? `${minutes}m${remainingSeconds}s`
          : `${minutes}m`;
      }

      const hours = Math.floor(seconds / 3600);
      const remainingMinutes = Math.floor((seconds % 3600) / 60);
      const remainingSeconds = seconds % 60;
      let result = `${hours}h`;
      if (remainingMinutes > 0) result += `${remainingMinutes}m`;
      if (remainingSeconds > 0) result += `${remainingSeconds}s`;
      return result;
    }

    if (Array.isArray(data)) {
      return data.map((item) => this.prepareForSend(item));
    }

    if (typeof data === "object") {
      const result: any = {};
      for (const [key, value] of Object.entries(data)) {
        // isIOSをisIosに変換（protobuf-esはis_ios→isIosに変換するため）
        const convertedKey = key === "isIOS" ? "isIos" : key;
        result[convertedKey] = this.prepareForSend(value);
      }
      return result;
    }

    return data;
  },
};

// Helper function to handle errors
export const handleProtoError = (error: any) => {
  console.error("Proto Client Error:", error);
  throw error;
};
